import { openForm } from '../utils/openform';
import { updateReferrerFields } from '../utils/updatereferrerfields';
import { closeAllForms } from '../utils/closeallforms';
import { getCSRFToken } from '../utils/getCSRFToken';
import { postJSONWithCSRFRetry } from '../utils/fetchWithCSRFRetry';
import { formatDateISO, addDays, parseDate } from '../utils/dateUtils';
import { safeJsonParse } from '../utils/authErrorHandler';

export default function registerUpdateBlockForm() {
  window.Alpine.store('editBlockItemForm', {
    // Store variables
    calendarId: '',
    blockItemId: '',
    unitId: '',
    startDate: '',
    endDate: '',
    initialStartDate: '',
    initialEndDate: '',
    product: '',
    flatpickrInstance: null,
    flatpickrInstances: {},
    unavailableDates: [], // format should be ['23-01-2024']
    categorizedDates: {
      start: [],
      single: [],
      middle: [],
      end: [],
    },

    // UPDATE BLOCK ITEM (item click)
    async handleUpdateBlockItemClick(e) {
      // Use currentTarget so we get the block item div (with data attrs), not a child like the inner span
      const blockItemEl =
        e.currentTarget ||
        e.target.closest(
          '.calendar-tour-block-item, .calendar-accomm-block-item',
        );
      if (!blockItemEl) {
        console.error('Block item element not found');
        return;
      }

      // Retrieve calendar ID from the calendar container
      const calendarContainer = document.querySelector('.calendar-container'); // Select the calendar container

      if (calendarContainer) {
        this.calendarId = calendarContainer.getAttribute('data-calendar-id'); // Retrieve calendar ID
      } else {
        console.error('Calendar container not found');
      }

      const blockItemId = blockItemEl.getAttribute('data-block-id');
      this.blockItemId = blockItemId;
      const blockName = blockItemEl.getAttribute('data-name');
      console.log('blockItemId:', blockItemId);
      console.log('blockName:', blockName);
      const unitId =
        blockItemEl.getAttribute('data-accomm-unit-id') ||
        blockItemEl.getAttribute('data-tour-unit-id');
      this.unitId = unitId;

      // Get dates
      let start_date = blockItemEl.getAttribute('data-start-date');
      this.initialStartDate = start_date;
      this.startDate = start_date;
      let end_date = blockItemEl.getAttribute('data-end-date');
      this.initialEndDate = end_date;
      this.endDate = end_date;
      // Convert string date to Date object to add a day later for check-out date
      let end_date_obj = parseDate(end_date);

      const formIdToUpdate = blockItemEl.classList.contains(
        'calendar-accomm-block-item',
      )
        ? 'updateAccommodationBlockForm'
        : 'updateTourBlockForm';

      this.product = blockItemEl.classList.contains(
        'calendar-accomm-block-item',
      )
        ? 'Accommodation'
        : 'Tour';

      closeAllForms();

      // Render the form
      await this.renderForm();

      // If accommodation booking add one day to end_date to be check-out day
      // This converts the stored date (checkout - 1) back to the actual checkout date for display
      if (formIdToUpdate === 'updateAccommodationBlockForm') {
        end_date = formatDateISO(addDays(end_date_obj, 1));
        this.endDate = end_date;
      }

      // Add 'block_item_id' to the fieldData object
      let fieldData;

      if (formIdToUpdate === 'updateAccommodationBlockForm') {
        fieldData = {
          name: blockName,
          block_item_id: blockItemId,
          accommodation_unit_id: unitId,
          start_date: start_date,
          end_date: end_date,
        };
      } else {
        fieldData = {
          name: blockName,
          block_item_id: blockItemId,
          tour_unit_id: unitId,
          start_date: start_date,
          end_date: end_date,
        };
      }

      // Update initial dates
      this.updateInitialDates(start_date, end_date);

      // Reset & Initialize Flatpickr (ensure it reinitializes every time the form is opened)
      const flatpickrElement = document.querySelector(
        `#${formIdToUpdate} [x-ref="datepicker"]`,
      );
      if (flatpickrElement) {
        this.resetFlatpickr(this.product, flatpickrElement);
      } else {
        console.log('Flatpickr element not found');
      }

      // Open the form with slight delay that form can be rendered and open with smooth transition
      setTimeout(() => {
        openForm(formIdToUpdate, 'open', fieldData, blockItemId);
        document.body.classList.add('slideform-open'); // This is to prevent the background from scrolling when the form is open
        document.querySelector(
          `#${formIdToUpdate} form`,
        ).action = `/bookings/block/${blockItemId}/update/`;

        updateReferrerFields();
      }, 100);
    },
    async renderForm() {
      // Render the form on the DOM
      let formContainer;
      let updateBlockForm;
      let url;

      if (this.product === 'Accommodation') {
        formContainer = document.getElementById(
          'update-accommodation-block-item-form-container',
        );
        updateBlockForm = document.getElementById(
          'updateAccommodationBlockForm',
        );
        url = `/calendar/update-accommodation-block-item-form/${this.calendarId}/`;
      } else if (this.product === 'Tour') {
        formContainer = document.getElementById(
          'update-tour-block-item-form-container',
        );
        updateBlockForm = document.getElementById('updateTourBlockForm');
        url = `/calendar/update-tour-block-item-form/${this.calendarId}/`;
      }

      if (updateBlockForm) {
        // Form is already rendered, just open it
        return; // Exit the function early
      }

      // Fetch the product form from the server
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'X-CSRFToken': getCSRFToken(),
        },
      });

      // Use safe JSON parsing to handle auth errors gracefully
      const data = await safeJsonParse(response);
      // Update the form container with the fetched HTML
      formContainer.innerHTML = data.update_block_form_html;
    },
    initializeFlatpickr(product, flatpickrElement) {
      this.flatpickrInstances[product] = flatpickr(flatpickrElement, {
        mode: 'range',
        dateFormat: 'd/m/Y',
        showMonths: 2, // Range picker always shows two months
        defaultDate: [this.initialStartDate, this.initialEndDate],

        onChange: (selectedDates) => {
          this.startDate = selectedDates[0]
            ? formatDateISO(selectedDates[0]) // Timezone-safe YYYY-MM-DD format
            : '';
          this.endDate = selectedDates[1]
            ? formatDateISO(selectedDates[1])
            : '';
          // Manually call updatePrice after updating the dates
          //this.product = product;
          this.updateFlatpickrDates(product);
        },
        onOpen: (selectedDates, dateStr, instance) => {
          this.product = product;
          // Disable all dates when initially opened
          instance.set('disable', [
            function () {
              return true; // Disable all dates initially
            },
          ]);
          const currentYear = instance.currentYear;
          const currentMonth = instance.currentMonth + 1; // +1 because getMonth() is zero-indexed
          // Fetch unavailable dates for the currently viewed month and year when the picker opens
          this.fetchUnavailableDates(
            currentYear.toString(),
            currentMonth.toString(),
            instance,
          );
        },
        onMonthChange: (selectedDates, dateStr, instance) => {
          // Disable all dates when month changes
          instance.set('disable', [
            function () {
              return true; // Disable all dates initially
            },
          ]);
          const currentYear = instance.currentYear;
          const currentMonth = instance.currentMonth + 1; // +1 because getMonth() is zero-indexed

          this.fetchUnavailableDates(
            currentYear.toString(),
            currentMonth.toString(),
            instance,
          );
        },
        onYearChange: (selectedDates, dateStr, instance) => {
          // Disable all dates when month changes
          instance.set('disable', [
            function () {
              return true; // Disable all dates initially
            },
          ]);
          const currentYear = instance.currentYear;
          const currentMonth = instance.currentMonth + 1; // Adjusted to be 1-indexed

          this.fetchUnavailableDates(
            currentYear.toString(),
            currentMonth.toString(),
            instance,
          );
        },
      });
    },
    updateFlatpickrDates(product) {
      const instance = this.flatpickrInstances[product];

      if (instance && instance.daysContainer) {
        // Disable middle dates to avoid user adding ranges that span them
        // STILL NEED TO FIGURE OUT HOW TO HANDLE SINGLE NIGHT BOOKINGS!!!
        instance.set('disable', this.unavailableDates);
        // Convert categorized_dates arrays into Set for efficient lookup
        console.log('categorizedDates:', this.categorizedDates);
        this.categorizedDates = {
          start: new Set(this.categorizedDates.start),
          single: new Set(this.categorizedDates.single),
          middle: new Set(this.categorizedDates.middle),
          end: new Set(this.categorizedDates.end),
        };
        // Get all day elements inside the Flatpickr calendar
        const dayElements =
          instance.daysContainer.querySelectorAll('.flatpickr-day');
        // Loop through all day elements
        dayElements.forEach((dayElem) => {
          // Get the date string for the day element and convert it
          const ariaLabel = dayElem.getAttribute('aria-label');
          const dateStr = this.convertAriaLabelToDateStr(ariaLabel); // Convert to DD-MM-YYYY format

          // Apply custom styling based on categorized dates
          if (this.categorizedDates.start.has(dateStr)) {
            dayElem.classList.add('start-date');
          }
          if (this.categorizedDates.single.has(dateStr)) {
            dayElem.classList.add('single-date');
          }
          if (this.categorizedDates.middle.has(dateStr)) {
            dayElem.classList.add('middle-date');
          }
          if (this.categorizedDates.end.has(dateStr)) {
            dayElem.classList.add('end-date');
          }
        });
      } else {
        console.error(`Flatpickr instance for '${product}' not found.`);
      }
    },
    fetchUnavailableDates(year, month, instance) {
      const csrfToken = getCSRFToken();

      // Block forms always use 2-month mode (range picker shows two months)
      let requestData = {
        year: year,
        month: month,
        unitId: this.unitId,
        blockItemId: this.blockItemId,
        amountUnits: this.units,
        two_month_mode: true,
      };
      // Disable date selection until availability has been fetched and updated
      this.disableFlatpickr(instance, true);

      postJSONWithCSRFRetry('/bookings_app/get-unavailable-dates/', requestData)
        .then(async (response) => {
          const data = await safeJsonParse(response);
          return data;
        })
        .then((data) => {
          this.categorizedDates = data.categorized_dates;
          this.unavailableDates = data.categorized_dates.middle;

          this.updateFlatpickrDates(this.product);
          // Re-enable date selection now that availability has been updated
          this.disableFlatpickr(instance, false);
        })
        .catch((error) => {
          console.error('Error fetching unavailable dates:', error);
        });
    },
    disableFlatpickr(flatpickrInstance, disable) {
      // Disable or enable the input field
      flatpickrInstance.input.disabled = disable;

      // Disable or enable the prev/next navigation buttons, if they exist
      if (flatpickrInstance.prevMonthNav && flatpickrInstance.nextMonthNav) {
        flatpickrInstance.prevMonthNav.disabled = disable;
        flatpickrInstance.nextMonthNav.disabled = disable;
      }

      // Optionally, disable the year and month dropdowns if present
      const pluginElements =
        flatpickrInstance.calendarContainer.querySelectorAll(
          '.flatpickr-monthDropdown-months, .numInput',
        );
      pluginElements.forEach((element) => {
        element.disabled = disable;
      });
    },
    resetFlatpickr(product, flatpickrElement) {
      if (this.flatpickrInstances[product]) {
        console.log(
          'Destroying existing Flatpickr instance for product:',
          product,
        );
        this.flatpickrInstances[product].destroy();
        flatpickrElement.value = ''; // Clear the input field
      }

      this.initializeFlatpickr(product, flatpickrElement);
    },
    updateInitialDateForIdentifier(product, isoDate) {
      if (this.flatpickrInstances[product]) {
        const newDate = parseDate(isoDate);
        if (newDate) {
          this.flatpickrInstances[product].jumpToDate(newDate, true);
        }
      }
    },
    updateInitialDates(isoStartDate, isoEndDate) {
      console.log('Updating Initial Dates...');
      this.initialStartDate = this.convertDateFormat(isoStartDate);
      this.initialEndDate = this.convertDateFormat(isoEndDate);
      if (this.flatpickrInstance) {
        this.flatpickrInstance.setDate(
          [this.initialStartDate, this.initialEndDate],
          true,
        );
      }
    },
    convertDateFormat(isoDate) {
      if (!isoDate) return '';
      const [year, month, day] = isoDate.split('-');
      return `${day}/${month}/${year}`;
    },
    convertAriaLabelToDateStr(ariaLabel) {
      const date = new Date(ariaLabel);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0'); // getMonth() is zero-indexed
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    },
    archiveBlockItem() {
      const csrfToken = getCSRFToken();
      fetch(`/bookings_app/calendar-delete-block-item/${this.blockItemId}/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
        },
      })
        .then(async (response) => {
          if (!response.ok) throw new Error('Failed to archive booking item');
          const data = await safeJsonParse(response);
          return data;
        })
        .then((data) => {
          console.log('Block item archived successfully:', data);
          this.removeDeletedBlockItemFromCalendar(data);
        })
        .catch((error) => {
          console.error('Error archiving block item:', error);
        });
    },
    removeDeletedBlockItemFromCalendar(data) {
      // Close the form
      this.closeForm();
      const calendarStore = Alpine.store('calendarStore');
      const toastStore = Alpine.store('toastStore');
      calendarStore.isLoading = true;

      // Delay the update of the calendarStore to allow the fade-out effect
      setTimeout(() => {
        // Remove the deleted block item from the calendar store
        const updatedBlockItems = calendarStore.blockItems.filter(
          (item) => item.item.id !== this.blockItemId,
        );

        // Remove the blocked dates associated with the deleted block item
        const updatedCalendarDatesBlocked = {};
        for (const unitId in calendarStore.calendarDatesBlocked) {
          const blockedUnit = calendarStore.calendarDatesBlocked[unitId];
          updatedCalendarDatesBlocked[unitId] = { dates: {} };
          for (const date in blockedUnit.dates) {
            updatedCalendarDatesBlocked[unitId].dates[date] = blockedUnit.dates[
              date
            ].filter((blockId) => blockId !== this.blockItemId);
            // If no blockIds remain for that date, delete the date entry
            if (updatedCalendarDatesBlocked[unitId].dates[date].length === 0) {
              delete updatedCalendarDatesBlocked[unitId].dates[date];
            }
          }
          // If no dates remain for that unit, delete the unit entry
          if (
            Object.keys(updatedCalendarDatesBlocked[unitId].dates).length === 0
          ) {
            delete updatedCalendarDatesBlocked[unitId];
          }
        }

        calendarStore.blockItems = updatedBlockItems;
        calendarStore.calendarDatesBlocked = updatedCalendarDatesBlocked;
        // Clear filter cache to ensure updated items are rendered
        calendarStore._bookingItemsCache = null;
        calendarStore._blockItemsCache = null;

        calendarStore.isLoading = false;
        toastStore.addToast(data.message, 'success');
      }, 1000); // Delay in milliseconds
    },
    closeForm() {
      closeAllForms();
      // Deactivate backdrop
      const backdrop = document.getElementById('backdrop');
      if (backdrop) {
        backdrop.style.display = 'none';
      }
      document.body.classList.remove('slideform-open'); // This is to prevent the background from scrolling when the form is open
    },
    validateForm(event) {
      const form = event.target.closest('form');

      // Ensure the start and end dates are defined
      if (!this.startDate || !this.endDate) {
        event.preventDefault();
        alert('Please select a valid start and end date.');
        return;
      }

      if (form.checkValidity()) {
        this.submitForm(event);
      } else {
        form.reportValidity();
      }
    },
    submitForm(event) {
      console.log('FORM SUBMITTED!!');
      this.closeForm();
      const calendarStore = Alpine.store('calendarStore');
      const toastStore = Alpine.store('toastStore');
      calendarStore.isLoading = true;
      const formElement = event.target.form || event.currentTarget.form;
      const formData = new FormData(formElement);

      console.log('formData:', formData);

      fetch(`/bookings_app/block/${this.blockItemId}/update/`, {
        method: 'POST',
        body: formData,
        headers: {
          'X-CSRFToken': getCSRFToken(),
        },
      })
        .then(async (response) => {
          const data = await safeJsonParse(response);
          return { status: response.status, body: data };
        })
        .then(({ status, body }) => {
          if (status >= 400) throw body;
          console.log('Block updated successfully:', body);

          // Extract the new block item from the response
          const newBlockItem = body.blockItem[0]; // Assuming the block item is in the first position of the array
          console.log('newBlockItem:', newBlockItem);
          // Validate newBlockItem structure
          if (!newBlockItem || !newBlockItem.item || !newBlockItem.item.id) {
            throw new Error('Invalid block item structure');
          }

          // Find the index of the original block item in the store
          const index = calendarStore.blockItems.findIndex(
            (item) => item.item.id === newBlockItem.item.id,
          );

          // Replace the original block item with the new one if it exists, otherwise add the new one
          let updatedBlockItems;
          if (index !== -1) {
            updatedBlockItems = [
              ...calendarStore.blockItems.slice(0, index),
              newBlockItem,
              ...calendarStore.blockItems.slice(index + 1),
            ];
          } else {
            updatedBlockItems = [...calendarStore.blockItems, newBlockItem];
          }
          calendarStore.blockItems = updatedBlockItems;
          // Clear filter cache to ensure updated items are rendered
          calendarStore._bookingItemsCache = null;
          calendarStore._blockItemsCache = null;

          console.log(
            'Updated calendarStore.blockItems:',
            calendarStore.blockItems,
          );

          calendarStore.isLoading = false;
          toastStore.addToast(body.message, 'success');

          // Clear fields only in the block form
          const blockFormId =
            this.product === 'Accommodation'
              ? 'updateAccommodationBlockForm'
              : 'updateTourBlockForm';
          const blockForm = document.getElementById(blockFormId);
          if (blockForm) {
            const fields = blockForm.getElementsByClassName('slideForm-field');
            Array.from(fields).forEach((field) => {
              field.value = '';
            });
          }
        })
        .catch((error) => {
          console.error('Error updating block:', error);
          calendarStore.isLoading = false;
          toastStore.addToast(
            error.message || 'An unexpected error occurred',
            'danger',
          );
        });
    },
  });
}
